import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT } from '@/constants';
import type { PeriodMetrics } from '@store/models';
import { usePlannerMetrics } from '@/hooks/usePlannerMetrics';
import { usePlannerMetricsExport } from '@/hooks/usePlannerMetricsExport';
import { BasicButton } from '@/components/common';
import {
  MetricTile,
  UtilizationCard,
  PeriodComparison,
  MetricsFilterBar,
} from '@/components/planner/metricsReport';
import { usePlannerMetricsReportScreenStyles } from './PlannerMetricsReportScreen.styles';

/**
 * Planner Module Metrics Report screen. Lets Planner/Admin users select
 * building(s) and one or two date ranges and view server-computed
 * requested-vs-completed counts, attendee totals, and space utilization, with
 * an optional side-by-side period comparison.
 */
export const PlannerMetricsReportScreen = () => {
  const styles = usePlannerMetricsReportScreenStyles();
  const { t } = useTranslation('metricsReport');
  const {
    buildingIds,
    periods,
    setBuildingIds,
    setPeriod,
    addComparisonPeriod,
    removeComparisonPeriod,
    hasComparison,
    canQuery,
    data,
    isFetching,
    isError,
  } = usePlannerMetrics();

  const { exportMetrics, isExporting, canExport } = usePlannerMetricsExport({
    buildingIds,
    period: periods[0],
  });

  // Export supports a single reporting period only; a comparison period disables it.
  const exportDisabledReason = hasComparison
    ? t('export.comparisonDisabledTooltip')
    : !canExport
      ? t('export.disabledTooltip')
      : undefined;
  const isExportDisabled = !canExport || isExporting || hasComparison;

  useEffect(() => {
    telemetry.trackPageView(TELEMETRY_COMPONENT, 'PlannerMetricsReportScreen');
  }, []);

  const resultPeriods = data?.periods ?? [];

  const renderPeriodMetrics = (period: PeriodMetrics, index: number) => (
    <div
      className={styles.periodBlock}
      data-testid="metrics-period"
      key={`${period.startDate}-${period.endDate}-${index}`}
    >
      {resultPeriods.length > 1 ? (
        <h2 className={styles.periodHeading}>
          {period.label ?? t('comparison.periodColumn', { index: index + 1 })}
        </h2>
      ) : null}
      <div className={styles.tileGrid}>
        <MetricTile
          label={t('metrics.meetings')}
          requestedValue={period.requestedMeetings}
          completedValue={period.completedMeetings}
        />
        <MetricTile
          label={t('metrics.attendees')}
          requestedValue={period.requestedAttendees}
          completedValue={period.completedAttendees}
        />
        <UtilizationCard
          label={t('metrics.meetingUtilization')}
          percentage={period.meetingUtilizationRate}
          applicable={period.meetingUtilizationApplicable}
          caption={t('utilization.meetingRateCaption')}
        />
        <UtilizationCard
          label={t('metrics.utilizationHours')}
          percentage={period.utilizationHoursPercentage}
          applicable={period.utilizationHoursApplicable}
          caption={t('utilization.hoursCaption')}
          detail={t('utilization.bookedHours', {
            hours: period.totalBookedHours.toLocaleString(),
          })}
        />
      </div>
    </div>
  );

  const renderBody = () => {
    if (!canQuery) {
      return <p className={styles.stateMessage}>{t('states.empty')}</p>;
    }
    if (isError) {
      return (
        <p className={`${styles.stateMessage} ${styles.errorMessage}`} role="alert">
          {t('states.error')}
        </p>
      );
    }
    if (isFetching) {
      return (
        <div className={styles.loadingWrapper} role="status">
          <Spinner size="medium" label={t('states.loading')} />
        </div>
      );
    }
    if (resultPeriods.length === 0) {
      return <p className={styles.stateMessage}>{t('states.empty')}</p>;
    }
    return (
      <div className={styles.periodsWrapper} data-testid="metrics-results" aria-live="polite">
        {resultPeriods.map(renderPeriodMetrics)}
        {resultPeriods.length === 2 && resultPeriods[0] && resultPeriods[1] ? (
          <PeriodComparison periods={[resultPeriods[0], resultPeriods[1]]} />
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.root} data-testid="metrics-report-screen" aria-busy={isFetching}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.description}>{t('description')}</p>
        </div>
        <BasicButton
          variant="primary"
          icon={<ArrowDownloadRegular />}
          disabled={isExportDisabled}
          title={exportDisabledReason}
          onClick={() => void exportMetrics()}
          data-testid="metrics-export-button"
        >
          {isExporting ? t('export.inProgress') : t('export.button')}
        </BasicButton>
      </header>
      <MetricsFilterBar
        buildingIds={buildingIds}
        onBuildingIdsChange={setBuildingIds}
        periods={periods}
        onPeriodChange={setPeriod}
        hasComparison={hasComparison}
        onAddComparison={addComparisonPeriod}
        onRemoveComparison={removeComparisonPeriod}
      />
      {renderBody()}
    </div>
  );
};

PlannerMetricsReportScreen.displayName = 'PlannerMetricsReportScreen';
